// M4 world §10 — deepwater, pre-salt, Arctic & sour gas (hooks into the M3 offshore model).
//   rig envelopes   6G/7G floaters, ultra-harsh jackup, Arctic semi, Caspian ice barge: M3 rigFitsWater / maxWaterByYear
//                   (data) — nothing to do here beyond the gates below.
//   dual activity   drillship_6g/7g: casing & BOP runs offline → a drilling day does 1/0.85 of the work (−15% well days).
//   BOP pulls       a floater whose subsea stack falls below 40% must pull it (14–28 d, rig on standby); a dual-BOP 7G
//                   runs its spare stack in 2 days instead.
//   subsea boosting  subsea_boosting (qi × reservoir subseaBoostingQiMult, default 1.3) and subsea_separation (qi × 1.1,
//                   −70% water to host): per-well installs on a tied-back subsea tree; re-applied as a qi multiplier.
//   pre-salt        salt sections creep: drilling time × 0.8 and a creep stuck-pipe roll (×2) without presalt_carbonates;
//                   reservoirs with co2Pct: the host must cut rate 30% without co2_reinjection (or a CO₂-removal FPSO).
//   Kashagan sour   H₂S ≥ 10,000 ppm: an h2sRated rig and an h2s_specialist on the crew.
//   Arctic          sea-ice fields: floaters (and bottom-founded rigs that are not ice-class) drill only in the season;
//                   Arctic floaters need an icebreaker; Alaska (Sept 2016 rule): a same-season relief rig on hand.
//   sanctions       techBan wells idle (worldPolitics).
import { allWells } from '../indexes.js';
import { chance } from '../../core/rng.js';
import { eqDef, fieldDef, stat, yf, unitPrice, isAvailable } from '../defs.js';
import { strataAt, stratumAt, reservoirsAt } from '../geo.js';
import { maxWaterFor } from '../../data/m3/equipment.js';
import { regimesAtM4, DEEPWATER_WELL, dayRateM4, lerpAt } from '../../data/m4/rules.js';
import { hasUpgrade } from '../research.js';
import { activeRigCrew } from '../crew.js';
import { problem, plotWater, rigBuiltYear, envelope } from '../m3/opsutil.js';
import { stickPipe } from '../rotary.js';
import { spend, notify, fmt$ } from '../util.js';
import { W, m4On, regionOf, month, ownWell } from './worldCore.js';
import { moratoriumAt, regimeSpudBlock, BOP_PULL_DAYS, DUAL_BOP_SWAP_DAYS, BOP_PULL_BELOW } from './worldRegime.js';
import { techBanReason } from './worldPolitics.js';
import { isMining } from './worldOilSands.js';

export const DUAL_ACTIVITY_TIME = 1 / 0.85;
// M4 balance: share of a shelf drilling day a floater makes in this much water (rules.DEEPWATER_WELL).
export const deepwaterPace = (waterFt) => (waterFt >= DEEPWATER_WELL.minWaterFt ? lerpAt(DEEPWATER_WELL.paceByWaterFt, waterFt) : 1);
export const SALT_ROP_MULT = 0.8;
export const SALT_CREEP_STUCK_PER_DAY = 0.003; // base creep stuck chance per salt day (est.); ×2 without the upgrade
export const CO2_RATE_CUT = 0.7;
export const SOUR_PPM = 10000;
const MONTHS = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const maxH2s = (fdef) => Math.max(0, ...(fdef?.prospects ?? []).flatMap((p) => (p.reservoirs ?? []).map((r) => r.h2s ?? 0)), ...(fdef?.strata ?? []).map((s) => s.h2s ?? 0));
const H2S_CACHE = new Map();
export const fieldH2s = (fid) => { if (!H2S_CACHE.has(fid)) H2S_CACHE.set(fid, maxH2s(fieldDef(fid))); return H2S_CACHE.get(fid); };
const iceOf = (fid) => fieldDef(fid)?.environment?.ice ?? null;
const isArctic = (fid) => fieldDef(fid)?.environment?.kind === 'arctic';
const gearDefs = (state, rig) => Object.values(rig?.gear ?? {}).map((uid) => (uid && state.items[uid] ? eqDef(state.items[uid].defId) : null)).filter(Boolean);
export const rigH2sRated = (state, rig, def) => !!stat(def, 'h2sRated', false) || gearDefs(state, rig).some((g) => stat(g, 'h2sRated', false));
export const hasH2sCrew = (state, rig) => !!rig && activeRigCrew(state, rig).some((c) => c.role === 'h2s_specialist');
export function hasIcebreaker(state, rig) {
  if (!rig) return false;
  return Object.values(state.logistics ?? {}).some((l) => l.assignedTo?.kind === 'rig' && l.assignedTo.id === rig.uid && stat(eqDef(l.defId), 'iceManagement', false));
}
function seaIceBlocks(def, fid, mon) {
  const ice = iceOf(fid);
  if (ice?.kind !== 'sea_ice' || !ice.drillSeason || ice.drillSeason.includes(mon)) return null;
  const s = def?.stats ?? {};
  if (!s.offshore) return null;
  if (s.floating || !s.iceClass) return `pack ice: ${s.floating ? 'floating rigs' : 'rigs that are not ice-class'} work only ${MONTHS[ice.drillSeason[0]]}–${MONTHS[ice.drillSeason[ice.drillSeason.length - 1]]} here.`;
  return null;
}

// ---- spud gate (opsactions.preAssign hunk) -------------------------------------------------------------------------
// null = no M4 objection; otherwise { ok: false, msg }.
export function worldPreAssign(state, rig, fieldId, plotIdx) {
  if (!m4On(state)) return null;
  const def = eqDef(rig?.defId);
  const water = plotWater(fieldId, plotIdx);
  const fresh = !state.fields[fieldId]?.plots?.[plotIdx]?.wellId;
  const fail = (msg) => ({ ok: false, msg });
  if (isMining(fieldId)) return fail(`${fieldDef(fieldId)?.name}: bitumen this shallow does not flow to a well — open a mine block instead (Field → oil sands).`);
  const tb = techBanReason(state, fieldId, water);
  if (tb) return fail(tb.charAt(0).toUpperCase() + tb.slice(1));
  const reg = regimeSpudBlock(state, fieldId, plotIdx, water, fresh);
  if (reg) return fail(reg);
  const fdef = fieldDef(fieldId);
  if (fieldH2s(fieldId) >= SOUR_PPM && !rigH2sRated(state, rig, def)) return fail(`${fdef?.name}: ${Math.round(fieldH2s(fieldId)).toLocaleString('en-US')} ppm H₂S — only a sour-service (H₂S-rated) rig may work here.`);
  const ice = seaIceBlocks(def, fieldId, month(state));
  if (ice && fresh) return fail(`${fdef?.name}: ${ice}`);
  if (water > 0 && isArctic(fieldId) && stat(def, 'floating', false) && !hasIcebreaker(state, rig)) return fail(`${fdef?.name}: contract an icebreaker / ice-management vessel to ${rig.name} first.`);
  if (water > 0 && regionOf(fieldId) === 'alaska' && fresh && regimesAtM4('alaska', state.day, water).some((g) => g.effect?.sameSeasonReliefRig)) {
    const relief = Object.values(state.rigs).some((r) => r.uid !== rig.uid && !r.wellId && envelope(eqDef(r.defId), rigBuiltYear(r)).offshore && !!stat(eqDef(r.defId), 'coldRated', false));
    if (!relief) return fail('Arctic rule (2016): keep a second winterized offshore rig idle as a same-season relief rig before you spud.');
  }
  return null;
}

// opsactions.charterRig hunk: chartered rigs face the same spud rules (no own rig: a contractor's def).
export function worldCharterBlock(state, defId, fieldId, plotIdx) {
  if (!m4On(state)) return null;
  const r = worldPreAssign(state, { uid: null, defId, gear: {} }, fieldId, plotIdx);
  if (r && /icebreaker|relief rig|H₂S-rated/.test(r.msg)) {
    // contractors bring their own ice management, relief plan and sour-service kit
    return null;
  }
  return r;
}

// ---- the drilling-day gate (m3 offshore.m3DrillGate hunk) --------------------------------------------------------------
// null (nothing) | { block: true } | { time } multiplier on today's drilling.
export function worldDrillGate(state, well, ctx) {
  if (!m4On(state) || !ownWell(well)) return null;
  const fid = well.fieldId;
  const water = well.waterDepthFt ?? plotWater(fid, well.plot);
  const tb = techBanReason(state, fid, water);
  if (tb) { problem(state, well, 'SANCTIONS', tb, { pause: true }); well.m4Suspended = 'sanctions'; return { block: true }; }
  const mor = water > 0 ? moratoriumAt(state, regionOf(fid), water) : null;
  if (mor) { problem(state, well, 'MORATORIUM', `drilling suspended by the deepwater moratorium (${mor.minWaterFt}+ ft) — the rig is on standby.`, { pause: false }); well.m4Suspended = mor.id; return { block: true }; }
  if (well.m4Suspended) well.m4Suspended = null;
  const def = ctx.rigDef;
  const s = def?.stats ?? {};
  const rig = ctx.rig;
  if (water > 0) {
    const ice = seaIceBlocks(def, fid, month(state));
    if (ice) { problem(state, well, 'ICE_SEASON', ice); return { block: true }; }
    if (rig && s.floating && isArctic(fid) && !hasIcebreaker(state, rig)) { problem(state, well, 'NO_ICEBREAKER', 'drifting ice on location — contract an icebreaker / ice-management vessel.', { pause: true }); return { block: true }; }
  }
  if (rig && !ctx.contractor && fieldH2s(fid) >= SOUR_PPM) {
    if (!rigH2sRated(state, rig, def)) { problem(state, well, 'H2S_RIG', `${Math.round(fieldH2s(fid)).toLocaleString('en-US')} ppm H₂S: the rig is not sour-service rated.`, { pause: true }); return { block: true }; }
    if (!hasH2sCrew(state, rig)) { problem(state, well, 'H2S_CREW', 'no H₂S safety specialist on the crew — the regulator stops work on a sour well.', { pause: true }); return { block: true }; }
  }
  // BOP pull on a floater (dual-BOP rigs swap stacks)
  if (rig && s.floating && rig.bopUid && state.items[rig.bopUid]) {
    const P = rig.m4bop;
    if (P && state.day < P.untilDay) { problem(state, well, 'BOP_PULL', `pulling the subsea BOP for repair (${P.untilDay - state.day} days left)${P.dual ? ' — running the spare stack' : ''}.`); return { block: true }; }
    if (P && state.day >= P.untilDay) { rig.m4bop = null; state.items[rig.bopUid].condition = Math.max(state.items[rig.bopUid].condition ?? 0, 90); }
    const bop = state.items[rig.bopUid];
    if (!P && (bop.condition ?? 100) < BOP_PULL_BELOW) {
      const dual = !!s.dualBop;
      const days = dual ? DUAL_BOP_SWAP_DAYS : BOP_PULL_DAYS[0] + ((state.day * 7 + (rig.uid?.length ?? 0)) % (BOP_PULL_DAYS[1] - BOP_PULL_DAYS[0] + 1));
      rig.m4bop = { startDay: state.day, untilDay: state.day + days, dual };
      state.company.stats.bopPulls = (state.company.stats.bopPulls ?? 0) + 1;
      problem(state, well, 'BOP_PULL', dual ? 'the subsea BOP failed its test — running the spare stack (2 days).' : `the subsea BOP failed its test — pulling the stack to the surface (${days} days on standby).`, { pause: !dual });
      return { block: true };
    }
  }
  let time = 1;
  if (s.dualActivity) time *= DUAL_ACTIVITY_TIME;
  // salt creep (offshore pre-salt & subsalt)
  if (water > 0) {
    const fdef = fieldDef(fid);
    const plot = state.fields[fid]?.plots?.[well.plot];
    if (fdef?.strata?.some((x) => x.salt) && plot) {
      const st = stratumAt(strataAt(fdef, plot), well.depth);
      if (st?.salt) {
        const tech = hasUpgrade(state, 'presalt_carbonates');
        if (!tech) time *= SALT_ROP_MULT;
        if (!state._rush && chance(state, SALT_CREEP_STUCK_PER_DAY * (tech ? 1 : 2))) { stickPipe(state, well, 'salt creep closed the hole on the string'); return { block: true }; }
      }
    }
  }
  // M4 balance: a floater in deep water drills slower (riser trips, more strings, deeper HPHT targets) and runs a
  // spread of services on top of the rig (DEEPWATER_WELL; the rig is ~45-55% of a deepwater well's cost)
  if (water >= DEEPWATER_WELL.minWaterFt && s.floating) {
    const rate = well.contract?.charter?.dayRate ?? dayRateM4(s.dayRateClass, yf(state));
    const spread = Math.round(rate * DEEPWATER_WELL.spreadShareOfRigRate);
    if (spread > 0) { spend(state, spread, 'logistics', false); well.cost += spread; }
    // the spread includes the operator's supply boats: a chartered rig (no player rig to contract them to) stays stocked
    if (!rig && state.m3?.supply) state.m3.supply[well.id] = Math.max(state.m3.supply[well.id] ?? 0, 5);
    time *= deepwaterPace(water);
  }
  return time !== 1 ? { time } : null;
}

// ---- subsea processing -------------------------------------------------------------------------------------------------
export const SUBSEA_PROCESSING = new Set(['subsea_boosting', 'subsea_separation']);
export function installSubseaProcessingImpl(state, wellId, defId) {
  const w = state.wells[wellId];
  if (!w) return { ok: false, msg: 'No such well.' };
  if (!m4On(state)) return { ok: false, msg: 'Subsea processing arrives after 2000.' };
  const def = eqDef(defId);
  if (!def || !SUBSEA_PROCESSING.has(defId)) return { ok: false, msg: 'Not a subsea processing unit.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} is not available until ${def.year}.` };
  if (!w.subsea) return { ok: false, msg: `${w.name} needs a subsea tree tied back to a host first.` };
  const kind = defId === 'subsea_boosting' ? 'boosting' : 'separation';
  if (w.m4subsea?.[kind]) return { ok: false, msg: `${w.name} already has ${def.name}.` };
  const water = w.waterDepthFt ?? plotWater(w.fieldId, w.plot);
  const rated = maxWaterFor(def, Math.floor(yf(state)));
  if (water > rated) return { ok: false, msg: `${def.name} is rated to ${rated.toLocaleString('en-US')} ft of water in ${Math.floor(yf(state))}.` };
  const price = unitPrice(state, def) * Math.pow(Math.max(1, water) / stat(def, 'refWaterFt', 5000), stat(def, 'costDepthExp', 0.2));
  if (!spend(state, price, 'capex')) return { ok: false, msg: `${def.name} costs ${fmt$(price)}.` };
  (w.m4subsea ??= {})[kind] = { defId, day: state.day, readyDay: state.day + stat(def, 'buildDays', 600), failedUntil: 0, cost: Math.round(price) };
  return { ok: true, msg: `${def.name} ordered for ${w.name} (${fmt$(price)}); online in ${stat(def, 'buildDays', 600)} days.` };
}

// qi multiplier from M4 world effects on this well (boosting, separation, pre-salt CO₂ handling).
export function worldQiMult(state, w) {
  let m = 1;
  const ss = w.m4subsea;
  const res = reservoirOf(state, w);
  if (ss) {
    const ok = (u) => u && state.day >= u.readyDay && !(u.failedUntil > state.day);
    if (ok(ss.boosting)) m *= res?.subseaBoostingQiMult ?? stat(eqDef('subsea_boosting'), 'qiMult', 1.3);
    if (ok(ss.separation)) m *= stat(eqDef('subsea_separation'), 'qiMult', 1.1);
  }
  if ((res?.co2Pct ?? 0) > 0 && !hasUpgrade(state, 'co2_reinjection')) {
    const host = w.platformUid ? state.platforms?.[w.platformUid] : null;
    if (!stat(eqDef(host?.defId), 'co2Removal', false)) m *= CO2_RATE_CUT;
  }
  return m;
}
const RES_CACHE = new WeakMap();
function reservoirOf(state, w) {
  if (!w.reservoirId) return null;
  let c = RES_CACHE.get(w);
  if (c && c.id === w.reservoirId) return c.r;
  const fdef = fieldDef(w.fieldId);
  const plot = state.fields[w.fieldId]?.plots?.[w.plot];
  const r = plot ? reservoirsAt(fdef, plot).find((x) => x.id === w.reservoirId) ?? null : null;
  RES_CACHE.set(w, { id: w.reservoirId, r });
  return r;
}
const WORLD_FIELDS = new Set();
export function worldMultField(fid) {
  if (WORLD_FIELDS.has(fid)) return true;
  const f = fieldDef(fid);
  const hit = !!f?.prospects?.some((p) => (p.reservoirs ?? []).some((r) => r.co2Pct > 0 || r.subseaBoostingQiMult));
  if (hit) WORLD_FIELDS.add(fid);
  return hit;
}

// Daily (tickPost): re-apply the multiplier as conditions change; roll subsea pump failures.
export function tickWellMults(state) {
  for (const w of allWells(state)) {
    if (w.status !== 'PRODUCING' || !w.prod || !ownWell(w)) continue;
    if (!w.m4subsea && !worldMultField(w.fieldId)) continue;
    const target = worldQiMult(state, w);
    const cur = w.m4q ?? 1;
    if (Math.abs(target - cur) > 1e-9) { w.prod.qi = Math.round(w.prod.qi / cur * target * 10) / 10; w.m4q = target; }
    const ss = w.m4subsea;
    if (ss) for (const k of ['boosting', 'separation']) {
      const u = ss[k];
      if (!u || state.day < u.readyDay || u.failedUntil > state.day) continue;
      const def = eqDef(u.defId);
      if (chance(state, stat(def, 'failPerYear', 0.1) / 365)) {
        u.failedUntil = state.day + 45;
        const fix = (u.cost ?? 0) * 0.05 * stat(def, 'interventionMult', 3);
        spend(state, fix, 'repairs', false);
        notify(state, 'problem', { wellId: w.id, fieldId: w.fieldId, problem: 'SUBSEA_FAIL', level: 'warn', text: `${w.name}: the ${def?.name} failed on the seabed — a vessel intervention (${fmt$(fix)}) takes about 45 days.` });
      }
    }
  }
}

// ---- view -----------------------------------------------------------------------------------------------------------------
export function deepwaterWellView(state, wellId) {
  const w = state.wells?.[wellId];
  if (!w) return null;
  const rig = w.rigId ? state.rigs[w.rigId] : null;
  const def = eqDef(rig?.defId ?? w.contract?.rigDefId);
  const res = reservoirOf(state, w);
  return {
    wellId, waterDepthFt: w.waterDepthFt ?? 0, suspended: w.m4Suspended ?? null, dualActivity: !!stat(def, 'dualActivity', false), dualBop: !!stat(def, 'dualBop', false),
    bopPull: rig?.m4bop ? { ...rig.m4bop, daysLeft: Math.max(0, rig.m4bop.untilDay - state.day) } : null,
    subsea: w.m4subsea ? JSON.parse(JSON.stringify(w.m4subsea)) : null, qiMult: m4On(state) ? Math.round(worldQiMult(state, w) * 1000) / 1000 : 1,
    co2Pct: res?.co2Pct ?? 0, co2Cut: (res?.co2Pct ?? 0) > 0 && m4On(state) && worldQiMult(state, { ...w, m4subsea: null }) < 1,
    h2sPpm: fieldH2s(w.fieldId), sour: fieldH2s(w.fieldId) >= SOUR_PPM, icebreaker: hasIcebreaker(state, rig),
    canBoost: m4On(state) && !!w.subsea && !w.m4subsea?.boosting && isAvailable(eqDef('subsea_boosting'), state),
    canSeparate: m4On(state) && !!w.subsea && !w.m4subsea?.separation && isAvailable(eqDef('subsea_separation'), state),
  };
}
